import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import { getFullImageUrl } from "../../utils/imageUrl";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetCategoriesQuery,
  useGetItemsQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "../../store/api";

const Categories = () => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const { data, isLoading, isError, error } = useGetCategoriesQuery(
    selectedStoreId,
    {
      skip: !selectedStoreId,
    },
  );
  const { data: items } = useGetItemsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [createCategory, createState] = useCreateCategoryMutation();
  const [updateCategory, updateState] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    sortOrder: 0,
    parentCategoryId: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    categoryId: null,
    categoryName: "",
  });
  const [confirmSaveModal, setConfirmSaveModal] = useState({
    isOpen: false,
    isEditing: false,
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const mutationErrorDetail =
    createState.error?.data?.detail || updateState.error?.data?.detail || "";
  const isImageDimensionsError =
    typeof mutationErrorDetail === "string" &&
    mutationErrorDetail.toLowerCase().includes("image dimensions");

  const categories = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const itemsList = Array.isArray(items) ? items : [];
  const filteredCategories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return categories;
    }
    return categories.filter((category) => {
      const haystack = `${category.name || category.categoryName || ""} ${category.description || ""}`
        .trim()
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [categories, searchQuery]);
  const {
    page,
    totalPages,
    totalCount,
    paginatedItems: paginatedCategories,
    setPage,
    setPageSize,
  } = useClientPagination(filteredCategories, {
    pageSize: 10,
    resetKey: searchQuery.trim().toLowerCase(),
  });
  const itemCountByCategoryId = itemsList.reduce((acc, item) => {
    if (!item?.categoryId) {
      return acc;
    }
    acc[item.categoryId] = (acc[item.categoryId] || 0) + 1;
    return acc;
  }, {});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetMutationFeedback = () => {
    if (typeof createState.reset === "function") {
      createState.reset();
    }
    if (typeof updateState.reset === "function") {
      updateState.reset();
    }
    setSuccessMessage("");
  };

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/image\/(png|jpg|jpeg)/)) {
        setErrorModal({
          isOpen: true,
          message: "Only PNG and JPG images are allowed",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setErrorModal({
          isOpen: true,
          message: "File size must be less than 2MB",
        });
        return;
      }
      setImageFile(file);
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveCategory = async (isEditing) => {
    if (!selectedStoreId) {
      setErrorModal({ isOpen: true, message: "Please select a store first" });
      return;
    }
    resetMutationFeedback();
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder),
        sort: Number(form.sortOrder),
        parentCategoryId: form.parentCategoryId || null,
      };
      if (isEditing) {
        await updateCategory({
          storeId: selectedStoreId,
          categoryId: editingId,
          payload,
          file: imageFile,
          removeImage,
        }).unwrap();
        setEditingId(null);
      } else {
        await createCategory({
          storeId: selectedStoreId,
          payload,
          file: imageFile,
        }).unwrap();
      }
      setSuccessMessage(
        `Category ${isEditing ? "updated" : "created"} successfully.`,
      );
      setForm({
        name: "",
        description: "",
        sortOrder: 0,
        parentCategoryId: "",
        isActive: true,
      });
      setShowForm(false);
      setImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
      setConfirmSaveModal({ isOpen: false, isEditing: false });
    } catch (err) {
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to save category.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmSaveModal({
      isOpen: true,
      isEditing: Boolean(editingId),
    });
  };

  const handleConfirmSave = async () => {
    await saveCategory(confirmSaveModal.isEditing);
  };

  const handleEdit = (category) => {
    resetMutationFeedback();
    setEditingId(category.categoryId || category.id);
    setForm({
      name: category.name || category.categoryName || "",
      description: category.description || "",
      sortOrder: category.sortOrder ?? category.sort ?? 0,
      parentCategoryId: category.parentCategoryId || "",
      isActive: category.isActive !== undefined ? category.isActive : true,
    });
    setImagePreview(getFullImageUrl(category.imageUrl));
    setImageFile(null);
    setRemoveImage(false);
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find(
      (c) => (c.categoryId || c.id) === categoryId,
    );
    setDeleteModal({
      isOpen: true,
      categoryId,
      categoryName: category?.name || category?.categoryName || "this category",
    });
  };

  const handleToggleActive = async (category) => {
    const categoryId = category.categoryId || category.id;
    try {
      await updateCategory({
        storeId: selectedStoreId,
        categoryId,
        payload: {
          name: category.name || category.categoryName || "",
          description: category.description || "",
          sortOrder: Number(category.sortOrder ?? category.sort ?? 0),
          sort: Number(category.sortOrder ?? category.sort ?? 0),
          parentCategoryId: category.parentCategoryId || null,
          isActive: !(category.isActive !== undefined
            ? category.isActive
            : true),
        },
      }).unwrap();
    } catch (err) {
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to update category status.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCategory({
        storeId: selectedStoreId,
        categoryId: deleteModal.categoryId,
      }).unwrap();
      setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete category. It may have items associated with it.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    resetMutationFeedback();
    setEditingId(null);
    setForm({
      name: "",
      description: "",
      sortOrder: 0,
      parentCategoryId: "",
      isActive: true,
    });
    setShowForm(false);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setConfirmSaveModal({ isOpen: false, isEditing: false });
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      headClassName: "w-[38%]",
      cellClassName: "align-top pr-3",
    },
    {
      key: "parent",
      label: "Parent Category",
      headClassName: "w-[20%]",
      cellClassName: "align-top pr-3 whitespace-normal break-words",
    },
    {
      key: "items",
      label: "Items",
      headClassName: "w-[14%]",
      cellClassName: "align-top pr-3",
    },
    {
      key: "status",
      label: "Status",
      headClassName: "w-[16%]",
      cellClassName: "align-top pr-3",
    },
    {
      key: "actions",
      label: "Actions",
      headClassName: "w-[12%] text-right",
      cellClassName: "align-top text-right",
    },
  ];

  const rows = paginatedCategories.map((category) => {
    const id = category.categoryId || category.id;
    const itemCount = itemCountByCategoryId[id] || 0;
    const imageSrc = getFullImageUrl(category.imageUrl);
    return {
      id,
      name: (
        <div className="flex items-start gap-3">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={category.name || category.categoryName || "Category image"}
              className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
              <svg
                className="h-5 w-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
          <div className="min-w-0 max-w-full">
            <p className="break-words text-sm font-semibold leading-5 text-slate-900">
              {category.name || category.categoryName}
            </p>
            {category.description && (
              <p
                className="mt-1 overflow-hidden break-words text-xs leading-5 text-slate-500"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {category.description}
              </p>
            )}
          </div>
        </div>
      ),
      parent: category.parentCategoryId
        ? categories.find(
            (c) => (c.categoryId || c.id) === category.parentCategoryId,
          )?.name || category.parentCategoryId
        : "-",
      items: (
        <button
          type="button"
          className="font-semibold text-[#4f46e5] hover:text-[#4338ca]"
          onClick={() =>
            navigate(
              `/menu/items?categoryId=${encodeURIComponent(id)}&from=categories`,
            )
          }
        >
          View {itemCount} {itemCount === 1 ? "item" : "items"}
        </button>
      ),
      status: (
        <div className="flex flex-wrap items-center gap-2">
          <ActionCheckbox
            checked={category.isActive !== undefined ? category.isActive : true}
            label={category.isActive ? "Active" : "Inactive"}
            onChange={() => handleToggleActive(category)}
          />
          <span className="text-sm text-slate-700">
            {category.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex justify-end gap-1">
          <ActionButton
            label="Edit"
            icon="edit"
            className="p-1"
            onClick={() => handleEdit(category)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            className="p-1"
            onClick={() => handleDelete(id)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Categories" subtitle="Manage menu categories." />
        <Card>
          <Alert tone="warning">
            Please select a store to manage categories.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categories"
        subtitle="Organize your menu into categories."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                resetMutationFeedback();
                setShowForm(true);
              }}
            >
              Add Category
            </Button>
          )
        }
      />
      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Category" : "Add Category"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Image Upload Section */}
            <div className="rounded-lg border border-slate-200 p-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Category Image
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Category preview"
                      className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                      <svg
                        className="h-8 w-8 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="mb-2 text-xs text-slate-600">
                    PNG or JPG, max 2MB, max 1024x1024px
                  </p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <span className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300">
                        Choose File
                      </span>
                    </label>
                    {imagePreview && !imageFile && editingId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setImagePreview(null);
                          setRemoveImage(true);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Category Name"
                name="name"
                placeholder="Appetizers"
                value={form.name}
                onChange={handleChange}
                required
              />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
                />
                Active
              </label>
            </div>

            <Select
              label="Parent Category"
              name="parentCategoryId"
              value={form.parentCategoryId}
              onChange={handleChange}
              helper="Optional parent for nested category structure"
            >
              <option value="">No parent category</option>
              {categories
                .filter(
                  (category) =>
                    (category.categoryId || category.id) !== editingId,
                )
                .map((category) => (
                  <option
                    key={category.categoryId || category.id}
                    value={category.categoryId || category.id}
                  >
                    {category.name || category.categoryName}
                  </option>
                ))}
            </Select>

            <Textarea
              label="Description"
              name="description"
              placeholder="Delicious starters to begin your meal"
              value={form.description}
              onChange={handleChange}
            />

            {successMessage && <Alert tone="success">{successMessage}</Alert>}
            {(createState.isError || updateState.isError) &&
              !isImageDimensionsError && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save category."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Category
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading categories...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load categories."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-end md:justify-between">
                  <div className="w-full md:max-w-xl">
                    <Input
                      label="Search by Name"
                      placeholder="Search categories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    Showing {filteredCategories.length} of {categories.length}{" "}
                    categories
                  </p>
                </div>
                <Table
                  columns={columns}
                  rows={rows}
                  tableClassName="table-fixed text-left"
                  emptyText={
                    searchQuery.trim()
                      ? "No categories match your search."
                      : "No categories yet."
                  }
                />
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    pageSize={10}
                    totalCount={totalCount}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                  />
                )}
              </>
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" })
        }
        title="Delete Category"
        confirmText="Delete"
        confirmTone="danger"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.categoryName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600">
          This action cannot be undone. The category cannot be deleted if it has
          items.
        </p>
      </Modal>

      <Modal
        isOpen={confirmSaveModal.isOpen}
        onClose={() =>
          setConfirmSaveModal({ isOpen: false, isEditing: false })
        }
        title={
          confirmSaveModal.isEditing ? "Update Category" : "Add Category"
        }
        confirmText={
          confirmSaveModal.isEditing ? "Yes, Update" : "Yes, Add"
        }
        onConfirm={handleConfirmSave}
        isLoading={createState.isLoading || updateState.isLoading}
      >
        <p>
          {confirmSaveModal.isEditing
            ? "Are you sure you want to update this category?"
            : "Are you sure you want to add this category?"}
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Categories;
